import { Tabs } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useParams } from 'react-router';
import { useCurrentUser } from '../../auth/CurrentUserProvider';
import { DetailPageSkeleton } from '../../components/DetailPageSkeleton';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { OwnerAssetsTab } from './OwnerAssetsTab';
import { OwnerDetailsForm } from './OwnerDetailsForm';
import { OwnerHistoryTab } from './OwnerHistoryTab';
import { useOwnerDetail } from './use-owner-detail';

/**
 * An owner's own page: fields on one tab, its trucks and trailers and history on the others —
 * an owner can hold both (gitaumoses4/chukua#15). `owner.manage` mutates the record; `owner.view`
 * alone sees everything read-only (CLAUDE.md: nav-hiding and disabled controls are UX only, the
 * server remains the authority).
 */
export function OwnerDetailPage() {
  const { id = '' } = useParams();
  const { state, updateError, saving, update } = useOwnerDetail(id);
  const { state: currentUser } = useCurrentUser();
  const canManage = currentUser.status === 'authenticated' && currentUser.user.permissions.includes('owner.manage');

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  if (state.status === 'loading') return <DetailPageSkeleton label="Loading owner" tabs={3} />;

  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Owner not found" description="This owner doesn't exist, or the link is out of date." />;
  }

  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this owner" description="Something went wrong loading this owner. Try reloading the page." />;
  }

  const { owner } = state;

  return (
    <>
      <PageHeader eyebrow="Assets" title={owner.name} description={owner.isCompany ? 'The company' : 'Investor'} />
      <Tabs
        items={[
          {
            key: 'details',
            label: 'Details',
            children: <OwnerDetailsForm owner={owner} canManage={canManage} error={updateError} saving={saving} onSave={update} />,
          },
          {
            key: 'assets',
            label: 'Trucks & trailers',
            children: (
              <OwnerAssetsTab
                trucksCurrent={owner.trucksCurrent}
                trucksPast={owner.trucksPast}
                trailersCurrent={owner.trailersCurrent}
                trailersPast={owner.trailersPast}
              />
            ),
          },
          { key: 'history', label: 'History', children: <OwnerHistoryTab ownerId={owner.id} /> },
        ]}
      />
    </>
  );
}
